import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from 'react-toastify';
import { useQueryClient } from 'shared-ui';

import { useMutation } from 'hooks/useMutation/useMutation';
import { useLocale } from 'hooks/useLocale/useLocale';
import { AppMessages } from 'i18n/messages';
import { useQuery } from 'hooks/useQuery/useQuery';
import { QueryKeyEnum } from 'core/global.enum';
import { fetchMailAccount, updateMailAccount } from 'api/mailAccount/mailAccount';
import type { MailAccountResponse } from 'api/mailAccount/mailAccount.types';

import { MailAccount } from './MailAccount';
import { MailAccountFormBody } from './MailAccount.types';
import { useMailAccountFormValidation } from './MailAccount.validation';

const responseToFormBody = (data: MailAccountResponse): Omit<MailAccountFormBody, 'password'> => ({
  address: data.address,
  username: data.username,
  authentication: data.authentication,
  port: data.port != null ? String(data.port) : '',
});

export const MailAccountContainer = () => {
  const { formatMessage } = useLocale();
  const { schema } = useMailAccountFormValidation();
  const queryClient = useQueryClient();
  const mailAccount = queryClient.getQueryData<MailAccountResponse>([QueryKeyEnum.MAIL_ACCOUNT]);

  const formMethods = useForm<MailAccountFormBody>({
    mode: 'onChange',
    resolver: yupResolver(schema),
    defaultValues: mailAccount
      ? { ...responseToFormBody(mailAccount), password: '' }
      : {
          address: '',
          username: '',
          authentication: 'SSL/TLS',
          port: '',
          password: '',
        },
  });

  const { isLoading: isLoadingMailAccount } = useQuery([QueryKeyEnum.MAIL_ACCOUNT], fetchMailAccount, {
    onSuccess: (data) => {
      formMethods.reset({ ...responseToFormBody(data), password: '' });
    },
  });

  const { mutate: onUpdateMailAccount, isLoading } = useMutation(updateMailAccount, {
    onSuccess: () => {
      toast.dark(formatMessage({ id: AppMessages['definitions.mailAccount.success'] }));
    },
  });

  const onSubmit = (body: MailAccountFormBody) => {
    const port = body.port ? parseInt(body.port, 10) : undefined;
    onUpdateMailAccount({ ...body, port });
  };

  return (
    <FormProvider {...formMethods}>
      <MailAccount onSubmit={onSubmit} isLoading={isLoading || isLoadingMailAccount} />
    </FormProvider>
  );
};
